import { LOOKUP_PAGE_TITLE } from '../constants';
import { ALL_FIELDS_REQUIRED } from './form';
import { DOB_ERROR_MSG, DOB_LABELS } from './dateOfBirth';
import { MODAL } from './identityInfo';

export const PAGE_TITLE = 'Enter prescription label information';
export const PAGE_TYPE = 'rxlabel';

export const TITLE = 'Enter their CVS Pharmacy® Rx info';
export const TITLE_SELF = 'Enter your \nprescription info';
export const TITLE_PET = 'Enter your pet\'s prescription info';

export const DESCRIPTION = 'We\'ll look for their records.';
export const DESCRIPTION_SELF = 'We\'ll look for your records.';
export const DESCRIPTION_PET = 'Provide the information your veterinarian has in their records.';

export const SUBMIT_BUTTON_TEXT = 'Search';
export const CANCEL_BUTTON_TEXT = 'Cancel';

export const READ_RX_LABEL_INFO = 'How to read a Rx label';
export const STORE_LOCATOR = 'Look up a store number';

export const RXLABEL_FORM_LABELS = {
  DATE_OF_BIRTH: 'Date of birth',
  DATE_OF_BIRTH_YYYY: 'Birth year',
  STORE_NUMBER: 'Store number',
  PRESCRIPTION_NUMBER: 'Rx number',
  CG_PRESCRIPTION_NUMBER: 'CVS Pharmacy Rx number'
};

export const RXLABEL_FORM_FIELD_IDS = {
  DATE_OF_BIRTH: 'dateOfBirth',
  STORE_NUMBER: 'storeNumber',
  PRESCRIPTION_NUMBER: 'prescriptionNumber'
};

export const RXLABEL_ERROR_TITLES = {
  DEFAULT: 'We need you to check something'
};

export const RXLABEL_VALIDATION = {
  DATE_OF_BIRTH_REQUIRED: 'Enter an 8-digit date of birth',
  STORE_NUMBER_REQUIRED: 'Enter a 3- to 5-digit store number',
  PRESCRIPTION_NUMBER_REQUIRED: 'Enter a 6- to 7-digit Rx number',
  DATE_OF_BIRTH_INVALID: 'Enter a valid 8-digit date of birth',
  STORE_NUMBER_INVALID: 'Enter a valid 3- to 5-digit store number',
  PRESCRIPTION_NUMBER_INVALID: 'Enter a valid 6- to 7-digit Rx number',
  DATE_OF_BIRTH_MINOR: 'Enter a date of birth at least 18 years in the past'
};

export const FORM_ALERT_HEADER = {
  DATE_OF_BIRTH: 'Date of birth is required',
  STORE_NUMBER: 'Store number is required',
  PRESCRIPTION_NUMBER: 'Prescription number is required'
};

export const RXLABEL_FORM_HELP_TEXT = {
  DATE_OF_BIRTH: 'MMDDYYYY',
  DATE_OF_BIRTH_YYYY: 'Enter 4 digits'
};

export const RXLABEL_FORM_MODAL_TEXT = {
  HEADER: 'Two ways to read your prescription label',
  LEFT_SUB_HEAD: 'Find the refill details section',
  LEFT_DESC: 'Use the store number and Rx number from the section with refill and pharmacy info.',
  RIGHT_SUB_HEAD: ' "Hi! It\'s your prescription talking" ',
  RIGHT_DESC: 'If you have a smart tagged prescription label our app can read the label for you.',
  RIGHT_LINK_DESC: 'Spoken Rx™ \n prescription reader',
  LEFT_LINK_DESC: 'Color-coded Rx labels',
  RXLABEL_LINK: 'https://www.cvs.com/content/pharmacy/rx-labels',
  SPOKEN_LINK: 'https://www.cvs.com/content/pharmacy/spoken-rx'
};

export const STORE_LOCATOR_MODAL_TEXT = {
  HEADER: 'Store Locator',
  SEARCH_BOX_LABEL: 'Search by ZIP code or city and state'
};

// Translation configuration for rxLabel page
export const RXLABEL_TRANSLATION_CONFIG = [
  { key: 'ALL_FIELDS_REQUIRED', value: ALL_FIELDS_REQUIRED, message: ALL_FIELDS_REQUIRED },
  { key: 'DOB_LABELS', value: DOB_LABELS, message: JSON.stringify(DOB_LABELS) },
  { key: 'DOB_ERROR_MSG', value: DOB_ERROR_MSG, message: JSON.stringify(DOB_ERROR_MSG) },
  { key: 'MODAL', value: MODAL, message: JSON.stringify(MODAL) },
  { key: 'PAGE_TITLE', value: PAGE_TITLE, message: PAGE_TITLE },
  { key: 'TITLE', value: TITLE, message: TITLE },
  { key: 'TITLE_SELF', value: TITLE_SELF, message: TITLE_SELF },
  { key: 'TITLE_PET', value: TITLE_PET, message: TITLE_PET },
  { key: 'DESCRIPTION', value: DESCRIPTION, message: DESCRIPTION },
  { key: 'DESCRIPTION_SELF', value: DESCRIPTION_SELF, message: DESCRIPTION_SELF },
  { key: 'DESCRIPTION_PET', value: DESCRIPTION_PET, message: DESCRIPTION_PET },
  { key: 'SUBMIT_BUTTON_TEXT', value: SUBMIT_BUTTON_TEXT, message: SUBMIT_BUTTON_TEXT },
  { key: 'CANCEL_BUTTON_TEXT', value: CANCEL_BUTTON_TEXT, message: CANCEL_BUTTON_TEXT },
  { key: 'READ_RX_LABEL_INFO', value: READ_RX_LABEL_INFO, message: READ_RX_LABEL_INFO },
  { key: 'STORE_LOCATOR', value: STORE_LOCATOR, message: STORE_LOCATOR },
  { key: 'RXLABEL_FORM_LABELS', value: RXLABEL_FORM_LABELS, message: JSON.stringify(RXLABEL_FORM_LABELS) },
  { key: 'RXLABEL_ERROR_TITLES', value: RXLABEL_ERROR_TITLES, message: JSON.stringify(RXLABEL_ERROR_TITLES) },
  { key: 'RXLABEL_VALIDATION', value: RXLABEL_VALIDATION, message: JSON.stringify(RXLABEL_VALIDATION) },
  { key: 'FORM_ALERT_HEADER', value: FORM_ALERT_HEADER, message: JSON.stringify(FORM_ALERT_HEADER) },
  { key: 'RXLABEL_FORM_HELP_TEXT', value: RXLABEL_FORM_HELP_TEXT, message: JSON.stringify(RXLABEL_FORM_HELP_TEXT) },
  { key: 'RXLABEL_FORM_MODAL_TEXT', value: RXLABEL_FORM_MODAL_TEXT, message: JSON.stringify(RXLABEL_FORM_MODAL_TEXT) },
  {
    key: 'STORE_LOCATOR_MODAL_TEXT',
    value: STORE_LOCATOR_MODAL_TEXT,
    message: JSON.stringify(STORE_LOCATOR_MODAL_TEXT)
  },
  { key: 'LOOKUP_PAGE_TITLE', value: LOOKUP_PAGE_TITLE, message: LOOKUP_PAGE_TITLE }
];

// Helper function to process translated items
export const processRxLabelTranslations = (translatedItems: any) => {
  if (!translatedItems || Object.keys(translatedItems).length === 0) {
    translatedItems = {};
  }

  const {
    [PAGE_TITLE]: PAGE_TITLE_TRANSLATED = PAGE_TITLE,
    [TITLE]: TITLE_TRANSLATED = TITLE,
    [TITLE_SELF]: TITLE_SELF_TRANSLATED = TITLE_SELF,
    [TITLE_PET]: TITLE_PET_TRANSLATED = TITLE_PET,
    [DESCRIPTION]: DESCRIPTION_TRANSLATED = DESCRIPTION,
    [DESCRIPTION_SELF]: DESCRIPTION_SELF_TRANSLATED = DESCRIPTION_SELF,
    [DESCRIPTION_PET]: DESCRIPTION_PET_TRANSLATED = DESCRIPTION_PET,
    [SUBMIT_BUTTON_TEXT]: SUBMIT_BUTTON_TEXT_TRANSLATED = SUBMIT_BUTTON_TEXT,
    [CANCEL_BUTTON_TEXT]: CANCEL_BUTTON_TEXT_TRANSLATED = CANCEL_BUTTON_TEXT,
    [READ_RX_LABEL_INFO]: READ_RX_LABEL_INFO_TRANSLATED = READ_RX_LABEL_INFO,
    [STORE_LOCATOR]: STORE_LOCATOR_TRANSLATED = STORE_LOCATOR
  } = translatedItems;

  // Parse complex objects
  const parseObject = (key: string, fallback: any) => {
    const parsed = translatedItems[JSON.stringify(fallback)];
    if (parsed) {
      try {
        return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      } catch {
        return fallback;
      }
    }
    return fallback;
  };

  return {
    ALL_FIELDS_REQUIRED: translatedItems[ALL_FIELDS_REQUIRED] || ALL_FIELDS_REQUIRED,
    DOB_LABELS: parseObject('DOB_LABELS', DOB_LABELS),
    DOB_ERROR_MSG: parseObject('DOB_ERROR_MSG', DOB_ERROR_MSG),
    MODAL: parseObject('MODAL', MODAL),
    PAGE_TITLE: PAGE_TITLE_TRANSLATED,
    TITLE: TITLE_TRANSLATED,
    TITLE_SELF: TITLE_SELF_TRANSLATED,
    TITLE_PET: TITLE_PET_TRANSLATED,
    DESCRIPTION: DESCRIPTION_TRANSLATED,
    DESCRIPTION_SELF: DESCRIPTION_SELF_TRANSLATED,
    DESCRIPTION_PET: DESCRIPTION_PET_TRANSLATED,
    SUBMIT_BUTTON_TEXT: SUBMIT_BUTTON_TEXT_TRANSLATED,
    CANCEL_BUTTON_TEXT: CANCEL_BUTTON_TEXT_TRANSLATED,
    READ_RX_LABEL_INFO: READ_RX_LABEL_INFO_TRANSLATED,
    STORE_LOCATOR: STORE_LOCATOR_TRANSLATED,
    LOOKUP_PAGE_TITLE: translatedItems[LOOKUP_PAGE_TITLE] || LOOKUP_PAGE_TITLE,
    RXLABEL_FORM_LABELS: parseObject('RXLABEL_FORM_LABELS', RXLABEL_FORM_LABELS),
    RXLABEL_ERROR_TITLES: parseObject('RXLABEL_ERROR_TITLES', RXLABEL_ERROR_TITLES),
    RXLABEL_FORM_MODAL_TEXT: parseObject('RXLABEL_FORM_MODAL_TEXT', RXLABEL_FORM_MODAL_TEXT),
    STORE_LOCATOR_MODAL_TEXT: parseObject('STORE_LOCATOR_MODAL_TEXT', STORE_LOCATOR_MODAL_TEXT),
    RXLABEL_FORM_HELP_TEXT: parseObject('RXLABEL_FORM_HELP_TEXT', RXLABEL_FORM_HELP_TEXT),
    RXLABEL_VALIDATION: parseObject('RXLABEL_VALIDATION', RXLABEL_VALIDATION),
    FORM_ALERT_HEADER: parseObject('FORM_ALERT_HEADER', FORM_ALERT_HEADER)
  };
};
